import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import * as departmentApi from '../../../lib/api/department';

const ProcessMasterDialog = ({ open, onClose, onSave, selectedProcess }) => {
  // 담당 팀 후보와, 고른 팀의 팀원.
  // 팀원을 팀 안에서만 고르게 해야 "NC팀인데 담당자는 영업팀 사람" 같은 조합이 안 생긴다.
  const [departments, setDepartments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedDpt, setSelectedDpt] = useState('');

  const [initialValues, setInitialValues] = useState({
    processCode: '',
    processName: '',
    processType: 'STANDARD',
    description: '',
    standardTime: 0,
    useYn: 'Y',
    // 이 공정을 맡는 팀과, 그 안의 주·부 담당자.
    // 주담당자가 휴가면 부담당자, 둘 다 휴가면 팀의 다른 사람이 배정된다.
    dptUuid: '',
    defaultWorkerUuid: '',
    subWorkerUuid: '',
  });

  useEffect(() => {
    if (selectedProcess) {
      setInitialValues({
        processCode: selectedProcess.processCd || selectedProcess.processCode || '',
        processName: selectedProcess.processNm || selectedProcess.processName || '',
        processType: selectedProcess.processTyp || selectedProcess.processType || 'STANDARD',
        description: selectedProcess.processDesc || selectedProcess.description || '',
        standardTime: selectedProcess.standardTime || 0,
        useYn: selectedProcess.useYn || 'Y',
        dptUuid: selectedProcess.dptUuid || '',
        defaultWorkerUuid: selectedProcess.defaultWorkerUuid || '',
        subWorkerUuid: selectedProcess.subWorkerUuid || '',
      });
    } else {
      setInitialValues({
        processCode: '',
        processName: '',
        processType: 'STANDARD',
        description: '',
        standardTime: 0,
        useYn: 'Y',
        dptUuid: '',
        defaultWorkerUuid: '',
        subWorkerUuid: '',
      });
    }
    // 팀원 목록을 받아오려면 어느 팀인지 따로 들고 있어야 한다.
    setSelectedDpt(selectedProcess?.dptUuid || '');
  }, [selectedProcess]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    departmentApi.getListDepartments({})
      .then((res) => { if (alive) setDepartments(res.data?.list || []); })
      .catch(() => { if (alive) setDepartments([]); });
    return () => { alive = false; };
  }, [open]);

  // 고른 팀의 팀원. 팀이 바뀌면 다시 받는다.
  useEffect(() => {
    if (!selectedDpt) {
      setTeamMembers([]);
      return;
    }
    let alive = true;
    departmentApi.getEmpsByDepart(selectedDpt, {})
      .then((res) => { if (alive) setTeamMembers(res.data?.list || []); })
      .catch(() => { if (alive) setTeamMembers([]); });
    return () => { alive = false; };
  }, [selectedDpt]);

  const validationSchema = Yup.object({
    processName: Yup.string().required('공정명을 입력해주세요'),
    processType: Yup.string().required('공정 유형을 선택해주세요'),
    standardTime: Yup.number()
      .min(0, '표준 시간은 0 이상이어야 합니다')
      .nullable(),
  });

  const handleSubmit = (values) => {
    onSave(values);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {selectedProcess ? '공정 수정' : '공정 추가'}
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={2}>
                {/* 공정 코드 */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="공정 코드"
                    name="processCode"
                    value={values.processCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.processCode && Boolean(errors.processCode)}
                    helperText={
                      (touched.processCode && errors.processCode) || 
                      '공정 코드는 자동 생성됩니다 (선택사항)'
                    }
                    disabled={!!selectedProcess}
                  />
                </Grid>

                {/* 공정 유형 */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required error={touched.processType && Boolean(errors.processType)}>
                    <InputLabel>공정 유형</InputLabel>
                    <Select
                      name="processType"
                      value={values.processType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="공정 유형"
                    >
                      <MenuItem value="DESIGN">설계</MenuItem>
                      <MenuItem value="STANDARD">표준</MenuItem>
                      <MenuItem value="ASSEMBLY">조립</MenuItem>
                      <MenuItem value="CUSTOM">커스텀</MenuItem>
                      <MenuItem value="INSPECTION">완품검사</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* 공정명 */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="공정명"
                    name="processName"
                    value={values.processName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.processName && Boolean(errors.processName)}
                    helperText={touched.processName && errors.processName}
                  />
                </Grid>

                {/* 공정 설명 */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="공정 설명"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="공정에 대한 상세 설명을 입력하세요"
                  />
                </Grid>

                {/* 표준 시간 */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="표준 시간 (분)"
                    name="standardTime"
                    value={values.standardTime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.standardTime && Boolean(errors.standardTime)}
                    helperText={touched.standardTime && errors.standardTime}
                    InputProps={{
                      inputProps: { min: 0 }
                    }}
                  />
                </Grid>

                {/* 사용 여부 */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>사용 여부</InputLabel>
                    <Select
                      name="useYn"
                      value={values.useYn}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="사용 여부"
                    >
                      <MenuItem value="Y">사용</MenuItem>
                      <MenuItem value="N">미사용</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* 담당 팀과 주·부 담당자 */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary">담당자</Typography>
                  </Divider>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    생산 공정이 만들어질 때 여기 정해둔 사람이 배정됩니다.
                    주담당자가 휴가면 부담당자가, 둘 다 휴가면 같은 팀의 다른 사람이 대신 들어갑니다.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>담당 팀</InputLabel>
                    <Select
                      name="dptUuid"
                      value={values.dptUuid}
                      label="담당 팀"
                      onChange={(e) => {
                        // 팀이 바뀌면 그 팀 사람이 아닌 담당자는 지운다.
                        // 안 지우면 "NC팀인데 담당자는 영업팀 사람" 같은 조합이 조용히 남는다.
                        setFieldValue('dptUuid', e.target.value);
                        setFieldValue('defaultWorkerUuid', '');
                        setFieldValue('subWorkerUuid', '');
                        setSelectedDpt(e.target.value);
                      }}
                    >
                      <MenuItem value=""><em>지정 안 함</em></MenuItem>
                      {departments.map((d) => (
                        <MenuItem key={d.dptUuid} value={d.dptUuid}>{d.dptNm}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {[
                  ['defaultWorkerUuid', '주담당자'],
                  ['subWorkerUuid', '부담당자'],
                ].map(([field, label]) => (
                  <Grid item xs={12} md={4} key={field}>
                    <FormControl fullWidth disabled={!values.dptUuid}>
                      <InputLabel>{label}</InputLabel>
                      <Select
                        name={field}
                        value={values.dptUuid ? values[field] : ''}
                        label={label}
                        onChange={handleChange}
                      >
                        <MenuItem value=""><em>지정 안 함</em></MenuItem>
                        {teamMembers.map((u) => (
                          <MenuItem key={u.usrUuid} value={u.usrUuid}>{u.usrNm}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}

                {values.dptUuid && teamMembers.length === 0 && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      이 팀에 등록된 인원이 없습니다. 부서관리에서 팀원을 넣어주세요.
                    </Alert>
                  </Grid>
                )}

                {/* 안내 메시지 */}
                <Grid item xs={12}>
                  <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      💡 등록한 공정은 워크플로우에서 선택하여 생산 프로세스를 구성할 수 있습니다.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose}>취소</Button>
              <Button type="submit" variant="contained">
                {selectedProcess ? '수정' : '추가'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ProcessMasterDialog;
